// RecaptureButton — Iter160f + iter169-Phase2 (Randy pt-ytre-recapture-range).
//
// Small 🔄 icon that opens a popover with TWO video-timestamp inputs
// (start + end). On submit, calls the parent's `onRecapture(startSec,
// endSec)` which is wired to POST /api/ytre/sessions/{sid}/entries/
// {eid}/recapture on the backend.
//
// Feb 2026 (Randy pt-ytre-recapture-range · iter169-Phase2) upgrades:
//   * Two guided inputs: START time + END time (was single input +
//     fixed 5-min-prior window)
//   * Auto-suggest button that pings the backend to compute the best
//     transcript range(s) for the family's dialogue. If multiple
//     ranges are returned, they render as clickable chips below the
//     inputs so the user can flip between them.
//   * Custom ranges fully supported — no more MM:SS-only error.
//
// Design goals per Randy:
//   * Icon sits next to the existing Edit button on every card header
//   * Accepts either MM:SS ("9:16") or HH:MM:SS ("1:15:32") or a bare
//     integer number of seconds ("136")
//   * Errors surface in the popover, not as a toast — user retries inline
//   * When auto-suggest returns nothing, fields blank + honest message

import { useState, useRef, useEffect, useCallback } from "react";
import { ArrowsClockwise, X, Check, MagicWand, CaretUp, CaretDown } from "@phosphor-icons/react";
import { runAsyncJob } from "../lib/asyncJob";

// Parse "9:16" / "62:36" / "1:15:32" / "136" → seconds. Null on garbage.
//
// Sep 10 2026 (Randy: "Im manually typing in the EXACT time stamp range
// ... and still getting some error"). Minutes used to be capped at 59 in
// BOTH shapes, so `62:36` — the notation YouTube transcripts themselves
// print — was rejected outright on any video past the one-hour mark.
// Randy: "one should not have to change to the HH:MM:SS format after
// breaching 1 hour. They could, and it should work. but I want it to
// still also work if they put in 62:37."
//
// Rules now:
//   • bare integer                → seconds ("3756")
//   • two parts  M:SS / MMM:SS    → minutes UNBOUNDED ("62:36" = 3756s)
//   • three parts H:MM:SS         → minutes/seconds must be < 60
export function parseTimestampInput(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // Bare integer seconds
  if (/^\d+$/.test(s)) return Math.max(0, parseInt(s, 10));
  const m = s.match(/^(?:(\d{1,3}):)?(\d{1,4}):(\d{1,2})$/);
  if (!m) return null;
  const ss = parseInt(m[3], 10);
  if (ss > 59) return null;
  if (m[1] != null) {
    // H:MM:SS — here minutes are a real clock field, so they must be < 60
    const mm = parseInt(m[2], 10);
    if (mm > 59) return null;
    return parseInt(m[1], 10) * 3600 + mm * 60 + ss;
  }
  // MM:SS — transcript notation, minutes deliberately uncapped
  return parseInt(m[2], 10) * 60 + ss;
}

// Accept a whole range pasted into one field — "62:37-67:37", the exact
// shape Randy reads off the transcript. Returns {start, end, rawStart,
// rawEnd} so each half can be re-rendered in the notation it was typed in.
export function parseRangeInput(raw) {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(.+?)\s*(?:-{1,2}|–|—|to|through)\s*(.+)$/i);
  if (!m) return null;
  const start = parseTimestampInput(m[1]);
  const end = parseTimestampInput(m[2]);
  if (start == null || end == null) return null;
  return { start, end, rawStart: m[1].trim(), rawEnd: m[2].trim() };
}

const fmtHMS = (sec) => {
  if (sec == null || sec < 0) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
};

// Uncapped MM:SS — 3756 → "62:36".
const fmtMinSec = (sec) =>
  `${Math.floor(Math.max(0, sec) / 60)}:${String(Math.max(0, sec) % 60).padStart(2, "0")}`;

// Re-render a value in whatever notation the user is already typing in, so
// the tick buttons never rewrite "62:36" into "1:02:36" underneath them.
const fmtLike = (raw, sec) => {
  const parts = String(raw ?? "").trim().split(":");
  if (parts.length >= 3) return fmtHMS(sec);
  if (parts.length === 1 && /^\d+$/.test(parts[0] || "")) return String(sec);
  return fmtMinSec(sec);
};

// Press-and-hold ramp. Sep 10 2026 (Randy: "pressing and holding the up
// arrow to speed dial all the way to over 1 hour is both not working but
// also ... the speed up ramping the tool is supposed to have was working
// (its not)"). TWO defects:
//
//   1. It never accumulated. The interval captured `onTick` from the
//      render that started the hold, and that closure read the input's
//      value out of a stale `startVal`. So every tick computed
//      `sameOldValue + step` — the display jumped once and then sat
//      there changing only when the step size changed. Fixed by ticking
//      through a functional state update (see `nudge` below), which
//      always sees the current value.
//   2. The schedule topped out at +60s per 100ms and only in four
//      discrete jumps. Now genuinely exponential: the step DOUBLES every
//      700ms held, so it walks 1s → 2 → 4 → 8 … and clears the two-hour
//      mark in ~7 seconds while still starting slow enough for
//      single-second precision in the first ~0.4s.
const TICK_MS = 60;
const RAMP_START_MS = 400;   // precision window before any ramping
const RAMP_DOUBLE_MS = 700;  // step doubles every 700ms held
const MAX_STEP = 600;        // never jump more than 10 video-minutes a tick

export function stepForHold(heldMs) {
  if (heldMs < RAMP_START_MS) return 0;
  const doublings = (heldMs - RAMP_START_MS) / RAMP_DOUBLE_MS;
  return Math.min(MAX_STEP, Math.max(1, Math.round(Math.pow(2, doublings))));
}

function TickChevron({ direction, onTick, disabled, "data-testid": testId, title }) {
  const holdRef = useRef({ timer: null, startedAt: 0 });

  const stop = useCallback(() => {
    const h = holdRef.current;
    if (h.timer) { clearInterval(h.timer); h.timer = null; }
    h.startedAt = 0;
  }, []);

  // A hold that runs this fast must never survive the release, including
  // a release that happens off the button or while the tab loses focus.
  useEffect(() => {
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
    window.addEventListener("touchcancel", stop);
    window.addEventListener("blur", stop);
    return () => {
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
      window.removeEventListener("touchcancel", stop);
      window.removeEventListener("blur", stop);
      stop();
    };
  }, [stop]);

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    stop();
    const sign = direction === "up" ? 1 : -1;
    onTick(sign);  // single tick on press
    holdRef.current.startedAt = Date.now();
    holdRef.current.timer = setInterval(() => {
      const step = stepForHold(Date.now() - holdRef.current.startedAt);
      if (step > 0) onTick(sign * step);
    }, TICK_MS);
  };

  return (
    <button
      type="button"
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={start}
      onTouchEnd={stop}
      onTouchCancel={stop}
      disabled={disabled}
      data-testid={testId}
      title={title}
      className="w-4 h-4 flex items-center justify-center rounded hover:bg-[#F0F1EE] disabled:opacity-30 disabled:cursor-not-allowed text-[#4A5D4E]"
    >
      {direction === "up" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
    </button>
  );
}

export default function RecaptureButton({ entryId, sessionId, currentAnchor, currentStart, currentEnd, onRecapture, disabled, hasChildren = true, testIdPrefix = "ytre-recapture" }) {
  const [open, setOpen] = useState(false);
  const [startVal, setStartVal] = useState("");
  const [endVal, setEndVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]); // [{start_sec, end_sec, reasoning}]
  const [suggestNote, setSuggestNote] = useState("");
  const [suggestFailed, setSuggestFailed] = useState(false);
  const [error, setError] = useState("");
  const startRef = useRef(null);

  // Seed the boxes in MM:SS — the notation the transcript prints, and
  // the notation Randy types.
  useEffect(() => {
    if (open) {
      // Seed inputs with the current range if we have one
      if (typeof currentStart === "number" && typeof currentEnd === "number" && currentEnd > 0) {
        setStartVal(fmtMinSec(currentStart));
        setEndVal(fmtMinSec(currentEnd));
      } else if (typeof currentAnchor === "number" && currentAnchor >= 0) {
        setStartVal(fmtMinSec(Math.max(0, currentAnchor - 300)));
        setEndVal(fmtMinSec(currentAnchor));
      } else {
        setStartVal("");
        setEndVal("");
      }
      setError("");
      setSuggestions([]);
      setSuggestNote("");
      setTimeout(() => startRef.current?.focus(), 0);
    }
  }, [open, currentAnchor, currentStart, currentEnd]);

  const applySuggestion = (s) => {
    // MM:SS, matching the notation the transcript itself prints, so the
    // numbers in the box read the same as the numbers Randy is comparing
    // them against.
    setStartVal(fmtMinSec(s.start_sec));
    setEndVal(fmtMinSec(s.end_sec));
    setError("");
  };

  // Tick handler shared by all four chevrons. The functional state update
  // is what makes press-and-hold accumulate — reading `startVal` from the
  // enclosing closure was the bug (see TickChevron's note).
  const nudge = (setter) => (step) => {
    setter((prev) => {
      const cur = parseTimestampInput(prev);
      return fmtLike(prev, Math.max(0, (cur == null ? 0 : cur) + step));
    });
    setError("");
  };

  // Typing/pasting a whole range into either field fills both. Each half
  // keeps the notation it was typed in — pasting "62:37-67:37" must not
  // come back as "1:02:37", that's the notation Randy is reading off the
  // transcript.
  const onRangeAwareChange = (setter) => (value) => {
    const range = parseRangeInput(value);
    if (range) {
      setStartVal(fmtLike(range.rawStart, range.start));
      setEndVal(fmtLike(range.rawEnd, range.end));
    } else {
      setter(value);
    }
    setError("");
  };

  const autoSuggest = async () => {
    if (!sessionId || !entryId) return;
    setSuggesting(true);
    setError("");
    setSuggestions([]);
    setSuggestNote("");
    setSuggestFailed(false);
    try {
      // Sep 10 2026 — was a plain POST that spent ~30s+ inside the
      // request on a 2-hour video and died in the ingress ("just spins
      // thinking and gives up"). Now a background job with polling.
      const data = await runAsyncJob(
        `/ytre/sessions/${sessionId}/entries/${entryId}/suggest-range`,
        null,
        {
          intervalMs: 2000,
          onProgress: (p) => { if (p?.label) setSuggestNote(p.label); },
        },
      );
      const ranges = data?.ranges || [];
      if (ranges.length === 0) {
        // Sep 11 2026 (Randy: "in production there is no popup, or toast
        // that shows up when it gives up") — a dead end is now flagged
        // as a failure, in red, with the reason.
        setSuggestFailed(true);
        setSuggestNote(data?.note || "Couldn't find a match — enter a range below.");
      } else {
        setSuggestions(ranges);
        applySuggestion(ranges[0]);
        setSuggestNote(
          ranges.length > 1
            ? `${ranges.length} candidate ranges — click a chip to switch.`
            : "",
        );
      }
    } catch (err) {
      setSuggestFailed(true);
      setSuggestNote(
        err?.response?.data?.detail || err?.message
        || "Auto-suggest failed — enter a range below.",
      );
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const startSec = parseTimestampInput(startVal);
    const endSec = parseTimestampInput(endVal);
    if (startSec == null || endSec == null) {
      setError("Use 62:36 (minutes:seconds), 1:02:36 (h:mm:ss), or plain seconds.");
      return;
    }
    if (endSec <= startSec) {
      setError("End time must be after start time.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onRecapture(startSec, endSec);
      setOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Recapture failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        data-testid={`${testIdPrefix}-btn-${entryId}`}
        title="Re-capture from a new timestamp range"
        className="p-1 rounded hover:bg-white disabled:opacity-40"
      >
        <ArrowsClockwise size={12} weight="duotone" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-40 bg-white border hairline rounded-lg shadow-lg p-3 w-80"
          data-testid={`${testIdPrefix}-popover-${entryId}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">
              New transcript range
            </div>
            <button
              type="button"
              onClick={autoSuggest}
              disabled={suggesting || busy || !hasChildren}
              data-testid={`${testIdPrefix}-suggest-${entryId}`}
              className="text-[10.5px] px-2 py-0.5 rounded border border-[#D8D6D2] hover:bg-[#F0F1EE] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              title={hasChildren
                ? "Find the transcript range that best matches this card's comments. Semantic match against the video's own transcript — no LLM, no cost."
                : "Nothing to match against yet. Auto-suggest works by comparing the comments on this card to the transcript, so it needs at least one comment or reply. Enter a range by hand below."}
            >
              {suggesting
                ? <ArrowsClockwise size={11} weight="duotone" className="animate-spin" />
                : <MagicWand size={11} weight="duotone" />}
              {suggesting ? "Reading transcript + comparing to dialogue…" : "Auto-suggest"}
            </button>
          </div>

          <form onSubmit={submit} className="space-y-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[10.5px] text-[#6B6A68] w-10 shrink-0">Start</span>
              <input
                ref={startRef}
                value={startVal}
                onChange={(e) => onRangeAwareChange(setStartVal)(e.target.value)}
                placeholder="62:36"
                disabled={busy}
                data-testid={`${testIdPrefix}-start-input-${entryId}`}
                className="w-[90px] shrink-0 bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-[12px] font-mono focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
              <div className="flex flex-col shrink-0" data-testid={`${testIdPrefix}-start-tick-${entryId}`}>
                <TickChevron
                  direction="up"
                  disabled={busy}
                  data-testid={`${testIdPrefix}-start-tick-up-${entryId}`}
                  title="Nudge start forward — press and hold to accelerate (reaches the 2-hour mark in a few seconds)"
                  onTick={nudge(setStartVal)}
                />
                <TickChevron
                  direction="down"
                  disabled={busy}
                  data-testid={`${testIdPrefix}-start-tick-down-${entryId}`}
                  title="Nudge start back — press and hold to accelerate"
                  onTick={nudge(setStartVal)}
                />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10.5px] text-[#6B6A68] w-10 shrink-0">End</span>
              <input
                value={endVal}
                onChange={(e) => onRangeAwareChange(setEndVal)(e.target.value)}
                placeholder="67:36"
                disabled={busy}
                data-testid={`${testIdPrefix}-end-input-${entryId}`}
                className="w-[90px] shrink-0 bg-[#FDFCF8] border hairline rounded-md px-2 py-1 text-[12px] font-mono focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
              <div className="flex flex-col shrink-0" data-testid={`${testIdPrefix}-end-tick-${entryId}`}>
                <TickChevron
                  direction="up"
                  disabled={busy}
                  data-testid={`${testIdPrefix}-end-tick-up-${entryId}`}
                  title="Nudge end forward — press and hold to accelerate (reaches the 2-hour mark in a few seconds)"
                  onTick={nudge(setEndVal)}
                />
                <TickChevron
                  direction="down"
                  disabled={busy}
                  data-testid={`${testIdPrefix}-end-tick-down-${entryId}`}
                  title="Nudge end back — press and hold to accelerate"
                  onTick={nudge(setEndVal)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-1.5 pt-1">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                data-testid={`${testIdPrefix}-cancel-${entryId}`}
                className="text-[11px] px-3 py-1 rounded text-[#6B6A68] hover:bg-[#F0F1EE]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !startVal.trim() || !endVal.trim()}
                data-testid={`${testIdPrefix}-submit-${entryId}`}
                className="text-[11px] px-3 py-1 rounded bg-[#2F6B3A] text-white hover:bg-[#245029] disabled:opacity-40 flex items-center gap-1"
              >
                <Check size={11} weight="bold" /> Recapture
              </button>
            </div>
          </form>

          {suggestions.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-suggestions-${entryId}`}>
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => applySuggestion(s)}
                  data-testid={`${testIdPrefix}-suggest-chip-${i}-${entryId}`}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-[#D8D6D2] hover:bg-[#F0F1EE] font-mono"
                  title={s.reasoning || ""}
                >
                  {fmtMinSec(s.start_sec)}–{fmtMinSec(s.end_sec)}
                </button>
              ))}
            </div>
          )}

          {suggestNote && (
            <div
              className={`text-[10.5px] mt-2 ${suggestFailed
                ? "text-[#C16E5A] font-medium not-italic"
                : "text-[#6B6A68] italic"}`}
              data-testid={`${testIdPrefix}-suggest-note-${entryId}`}
            >
              {suggestFailed ? "⚠ " : ""}{suggestNote}
            </div>
          )}

          {error && (
            <div className="text-[10.5px] text-[#C16E5A] mt-1.5" data-testid={`${testIdPrefix}-error-${entryId}`}>
              {error}
            </div>
          )}

          <div className="text-[10px] text-[#6B6A68] mt-2 italic">
            Accepts <span className="font-mono not-italic">62:36</span>,{" "}
            <span className="font-mono not-italic">1:02:36</span> or plain
            seconds — and you can paste a whole{" "}
            <span className="font-mono not-italic">62:37-67:37</span> range
            into either box. The card re-slices to match and moves itself
            into place on the canvas.
          </div>
        </div>
      )}
    </div>
  );
}
